import { SITE, OFFICES } from '../config/media';

const _postalAddress = (o) => ({
    '@type': 'PostalAddress',
    streetAddress: o.streetAddress,
    addressLocality: o.addressLocality,
    addressRegion: o.addressRegion,
    postalCode: o.postalCode,
    addressCountry: o.addressCountry,
});

export const ABS_URL = (path = '/') => `${SITE.url}${path}`;

export const orgJsonLd = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    legalName: SITE.name,
    url: SITE.url,
    foundingDate: '1955',
    description: SITE.description,
    telephone: SITE.phone,
    email: SITE.salesEmail,
    parentOrganization: {
        '@type': 'Organization',
        name: SITE.parentOrg,
    },
    address: OFFICES.map(_postalAddress),
    location: OFFICES.map((o) => ({
        '@type': 'Place',
        name: `${o.addressLocality} Office`,
        address: _postalAddress(o),
    })),
    areaServed: [
        { '@type': 'Country', name: 'Canada' },
        { '@type': 'AdministrativeArea', name: 'British Columbia' },
        { '@type': 'AdministrativeArea', name: 'Alberta' },
        { '@type': 'AdministrativeArea', name: 'Saskatchewan' },
        { '@type': 'AdministrativeArea', name: 'Manitoba' },
        { '@type': 'AdministrativeArea', name: 'Yukon' },
        { '@type': 'AdministrativeArea', name: 'Northwest Territories' },
    ],
    sameAs: [
        SITE.url,
    ],
});

export const websiteJsonLd = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    url: SITE.url,
});

export const productJsonLd = ({ name, description, slug, material, additionalProperty }) => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    brand: { '@type': 'Brand', name: SITE.name },
    manufacturer: {
        '@type': 'Organization',
        name: SITE.name,
        address: OFFICES.map(_postalAddress),
    },
    url: ABS_URL(slug),
    ...(material ? { material } : {}),
    ...(additionalProperty
        ? {
              additionalProperty: additionalProperty.map((p) => {
                  const base = { '@type': 'PropertyValue', name: p.name };
                  if (p.value !== undefined) base.value = p.value;
                  if (p.minValue !== undefined) base.minValue = p.minValue;
                  if (p.maxValue !== undefined) base.maxValue = p.maxValue;
                  if (p.unitText !== undefined) base.unitText = p.unitText;
                  return base;
              }),
          }
        : {}),
    areaServed: [
        { '@type': 'Country', name: 'Canada' },
        { '@type': 'AdministrativeArea', name: 'British Columbia' },
        { '@type': 'AdministrativeArea', name: 'Alberta' },
        { '@type': 'AdministrativeArea', name: 'Saskatchewan' },
        { '@type': 'AdministrativeArea', name: 'Manitoba' },
        { '@type': 'AdministrativeArea', name: 'Yukon' },
        { '@type': 'AdministrativeArea', name: 'Northwest Territories' },
    ],
});

export const breadcrumbJsonLd = (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: it.name,
        item: ABS_URL(it.path),
    })),
});

export const faqJsonLd = (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
});

export const aboutPageJsonLd = () => ({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `About ${SITE.name}`,
    url: ABS_URL('/about'),
});

export const contactPageJsonLd = () => ({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: `Contact ${SITE.name}`,
    url: ABS_URL('/contact'),
});

// Build a Person JSON-LD node for a sales-team member.
// Accepts { name, jobTitle, telephone?, email?, linkedin? }
export const personJsonLd = ({ name, jobTitle, telephone, email, linkedin }) => {
    const node = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name,
        jobTitle,
        worksFor: {
            '@type': 'Organization',
            name: SITE.name,
            url: SITE.url,
        },
    };
    if (telephone) node.telephone = telephone;
    if (email) node.email = email;
    if (linkedin) node.sameAs = [linkedin];
    return node;
};
